import IconButton from '@mui/material/IconButton'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import { Box, Button, TextField } from '@mui/material'

/**
 * A React component for editing key-value fields. It allows users to add, edit, and remove fields dynamically.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {Array<{ key: string, value: string }>} props.fields - The array of key-value pairs to be displayed and edited.
 * @param {function(number, string): function(Event): void} props.onFieldChange - Callback function to handle changes to a field.
 *        It takes the index of the field and the property ('key' or 'value') as arguments and returns a function to handle the event.
 * @param {function(number): void} props.onRemoveField - Callback function to handle the removal of a field.
 *        It takes the index of the field to be removed as an argument.
 * @param {function(): void} props.onAddField - Callback function to handle the addition of a new field.
 *
 * @returns {JSX.Element} The rendered KeyValueFieldsEditor component.
 */

export default function KeyValueFieldsEditor({
  fields,
  onFieldChange,
  onRemoveField,
  onAddField,
}) {
  return (
    <Box>
      {fields.map((field, idx) => (
        <Box
          key={idx}
          sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
        >
          <TextField
            label="Key"
            value={field.key}
            onChange={onFieldChange(idx, 'key')}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Value"
            value={field.value}
            onChange={onFieldChange(idx, 'value')}
            size="small"
            sx={{ flex: 2 }}
          />
          <IconButton
            title="Remove"
            variant="outlined"
            color="error"
            size="small"
            sx={{ minWidth: 32, ml: 1 }}
            onClick={() => onRemoveField(idx)}
            disabled={fields.length === 1}
          >
            <DeleteOutline
              fontSize="small"
              color="error"
              sx={{ '&:hover': { opacity: 0.6 } }}
            />
          </IconButton>
        </Box>
      ))}
      <Button
        variant="outlined"
        color="primary"
        size="small"
        sx={{ mt: 1 }}
        onClick={onAddField}
      >
        Add Field
      </Button>
    </Box>
  )
}
